import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { getStateName } from '../utils/zipToState';
import { ProviderSalesTaxRate } from '../utils/salesTax';

export const SALES_TAX_COVERAGE_ATTESTATION =
  'I confirm that the states listed above include every state in which I am responsible for collecting and remitting sales tax on services performed through LawnHub.';

interface SalesTaxRatesEditorProps {
  rates: ProviderSalesTaxRate[];
  onChange: (rates: ProviderSalesTaxRate[]) => void;
  disabled?: boolean;
  emptyHint?: string;
  /** Required attestation when rate rows are shown */
  coverageAttested?: boolean;
  onCoverageAttestedChange?: (attested: boolean) => void;
}

/** Display as a percent figure (6.25), never a decimal rate (0.0625). */
function percentToDisplay(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '0';
  return String(Math.round(n * 1000) / 1000);
}

function parsePercentInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '.') return null;
  if (!/^\d*\.?\d{0,3}$/.test(trimmed)) return null;
  const n = parseFloat(trimmed);
  if (!Number.isFinite(n)) return null;
  return Math.min(20, Math.max(0, Math.round(n * 1000) / 1000));
}

/**
 * Per-state residential / commercial sales tax %.
 * Values are percents (e.g. 6.25), not decimals (0.0625). No spinner arrows.
 */
const SalesTaxRatesEditor: React.FC<SalesTaxRatesEditorProps> = ({
  rates,
  onChange,
  disabled,
  emptyHint = 'Add service area ZIP codes to set sales tax rates by state.',
  coverageAttested = false,
  onCoverageAttestedChange,
}) => {
  const [drafts, setDrafts] = useState<
    Record<string, { residential: string; commercial: string }>
  >({});
  const ratesKey = rates.map((r) => r.state).join(',');

  // When states appear/disappear (ZIP changes), sync drafts for new rows from parent rates
  useEffect(() => {
    setDrafts((prev) => {
      const next: Record<string, { residential: string; commercial: string }> = {};
      for (const row of rates) {
        next[row.state] = prev[row.state] || {
          residential: percentToDisplay(row.residentialPercent),
          commercial: percentToDisplay(row.commercialPercent),
        };
      }
      return next;
    });
  }, [ratesKey, rates]);

  const pushRate = (
    state: string,
    field: 'residentialPercent' | 'commercialPercent',
    percent: number
  ) => {
    onChange(rates.map((r) => (r.state === state ? { ...r, [field]: percent } : r)));
  };

  const updateDraft = (
    state: string,
    field: 'residential' | 'commercial',
    value: string
  ) => {
    if (value !== '' && !/^\d*\.?\d{0,3}$/.test(value)) return;

    setDrafts((prev) => ({
      ...prev,
      [state]: {
        residential: prev[state]?.residential ?? '0',
        commercial: prev[state]?.commercial ?? '0',
        [field]: value,
      },
    }));

    // Don't commit incomplete typing like "6." yet
    if (value.endsWith('.')) return;
    const parsed = parsePercentInput(value);
    if (parsed === null) return;
    pushRate(
      state,
      field === 'residential' ? 'residentialPercent' : 'commercialPercent',
      parsed
    );
  };

  const commitDraft = (state: string, field: 'residential' | 'commercial') => {
    const draft = drafts[state]?.[field] ?? '';
    const parsed = parsePercentInput(draft) ?? 0;
    setDrafts((prev) => ({
      ...prev,
      [state]: {
        residential: prev[state]?.residential ?? '0',
        commercial: prev[state]?.commercial ?? '0',
        [field]: percentToDisplay(parsed),
      },
    }));
    pushRate(
      state,
      field === 'residential' ? 'residentialPercent' : 'commercialPercent',
      parsed
    );
  };

  if (!rates.length) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {emptyHint}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Sales tax by state
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        States are derived from your service area ZIP codes. Confirm every state where you collect and
        remit sales tax appears below, then enter residential and commercial rates as a percent (e.g.
        6.25 for 6.25%, not 0.0625). Enter 0 if you do not collect in that state (checkout still shows
        Sales tax $0.00). You are solely responsible for remittance.
      </Typography>
      {rates.map((row) => {
        const draft = drafts[row.state] || {
          residential: percentToDisplay(row.residentialPercent),
          commercial: percentToDisplay(row.commercialPercent),
        };
        return (
          <Box
            key={row.state}
            sx={{
              mb: 1.5,
              p: 1.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {getStateName(row.state)} ({row.state})
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Residential"
                  type="text"
                  size="small"
                  fullWidth
                  value={draft.residential}
                  disabled={disabled}
                  inputMode="decimal"
                  onChange={(e) => updateDraft(row.state, 'residential', e.target.value)}
                  onBlur={() => commitDraft(row.state, 'residential')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Homes, condos, HOA, vacant lots — e.g. 6.25"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Commercial"
                  type="text"
                  size="small"
                  fullWidth
                  value={draft.commercial}
                  disabled={disabled}
                  inputMode="decimal"
                  onChange={(e) => updateDraft(row.state, 'commercial', e.target.value)}
                  onBlur={() => commitDraft(row.state, 'commercial')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Commercial properties — e.g. 6.25"
                />
              </Grid>
            </Grid>
          </Box>
        );
      })}
      {onCoverageAttestedChange && (
        <FormControlLabel
          sx={{ mt: 1, alignItems: 'flex-start' }}
          control={
            <Checkbox
              checked={coverageAttested}
              onChange={(e) => onCoverageAttestedChange(e.target.checked)}
              disabled={disabled}
              sx={{ pt: 0.5 }}
            />
          }
          label={
            <Typography variant="body2" color="text.primary">
              {SALES_TAX_COVERAGE_ATTESTATION}
            </Typography>
          }
        />
      )}
    </Box>
  );
};

export default SalesTaxRatesEditor;
